<template>
    <main class="flex-1 overflow-y-auto focus:outline-none">
        <div class="py-6">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                <h1 class="text-2xl font-semibold text-gray-900">
                    {{ isEdit ? 'Upraviť FAQ' : 'Pridať nové FAQ' }}
                </h1>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                <div class="py-4">
                    <form @submit.prevent="saveFaq" class="space-y-8 divide-y divide-gray-200">
                        <div class="space-y-6">
                            <div>
                                <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                    <div class="sm:col-span-6">
                                        <label for="title" class="block text-sm font-medium text-gray-700">Názov</label>
                                        <div class="mt-1">
                                            <input type="text" id="title" v-model="faq.title"
                                                class="shadow-sm focus:ring-teal-500 focus:border-teal-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                placeholder="Zadajte názov FAQ" />
                                        </div>
                                        <p v-if="errors.title" class="mt-2 text-sm text-red-600">{{ errors.title }}</p>
                                    </div>
                                    <div class="sm:col-span-6">
                                        <label for="content"
                                            class="block text-sm font-medium text-gray-700">Obsah</label>
                                        <div class="mt-1">
                                            <textarea id="content" v-model="faq.content" rows="5"
                                                class="shadow-sm focus:ring-teal-500 focus:border-teal-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                placeholder="Zadajte obsah FAQ"></textarea>
                                        </div>
                                        <p v-if="errors.content" class="mt-2 text-sm text-red-600">{{ errors.content }}
                                        </p>
                                    </div>
                                    <div class="sm:col-span-2">
                                        <label for="position" class="block text-sm font-medium text-gray-700">Pozícia
                                            (nepovinné)</label>
                                        <div class="mt-1 flex items-center space-x-2">
                                            <input type="number" id="position" v-model.number="faq.position" min="1"
                                                max="999"
                                                class="shadow-sm focus:ring-teal-500 focus:border-teal-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                placeholder="1, 2, 3, ..." />
                                            <button type="button" @click="useNextAvailablePosition"
                                                class="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                                                Použiť #{{ nextAvailablePosition }}
                                            </button>
                                        </div>
                                        <p class="mt-2 text-sm"
                                            :class="showPositionWarning ? 'text-amber-600' : 'text-gray-500'">
                                            <span v-if="showPositionWarning">
                                                <strong>Upozornenie:</strong> Viacero FAQ s rovnakou pozíciou (999) bude
                                                zoradených podľa dátumu (novšie hore).
                                            </span>
                                            <span v-else>
                                                Číslo určujúce poradie FAQ (1 je prvé). Ak nevyplníte, bude FAQ
                                                zobrazené na konci zoznamu.
                                            </span>
                                        </p>
                                        <p class="mt-1 text-xs text-gray-500">
                                            Odporúčaná: #{{ nextAvailablePosition }} (nasledujúce dostupné číslo)
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="pt-5">
                            <div class="flex justify-end">
                                <button type="button" @click="router.push({ name: 'Zoznam FAQ' })"
                                    class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                                    Zrušiť
                                </button>
                                <button type="submit"
                                    class="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                                    :disabled="loading">
                                    <span v-if="loading">Ukladá sa...</span>
                                    <span v-else>Uložiť</span>
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </main>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axiosClient from '../axios'
import type { Faq } from '../types'

const router = useRouter()
const route = useRoute()
const loading = ref(false)
const faqId = computed(() => route.params.id)
const isEdit = computed(() => !!faqId.value)
const existingFaqs = ref<Faq[]>([])
const nextAvailablePosition = ref(1)
const showPositionWarning = computed(() => faq.position === 999)

const faq = reactive<Partial<Faq>>({
    title: '',
    content: '',
    position: 999 // Default position (will be displayed at the end)
})

const errors = reactive({
    title: '',
    content: ''
})

// Fetch all FAQs to determine positions
async function fetchAllFaqs() {
    try {
        const response = await axiosClient.get('/faqs/getAllFaqs')
        if (response.data && response.data.success) {
            existingFaqs.value = response.data.data.data || []
            calculateNextPosition()
        }
    } catch (error) {
        console.error('Error fetching FAQs for position calculation:', error)
    }
}

// Calculate the next available position (lowest unused number)
function calculateNextPosition() {
    if (existingFaqs.value.length === 0) {
        nextAvailablePosition.value = 1
        return
    }

    // Get all used positions
    const positions = existingFaqs.value.map(f => f.position || 999)

    // Find the lowest unused position starting from 1
    let pos = 1
    while (positions.includes(pos)) {
        pos++
    }
    nextAvailablePosition.value = pos
}

// Set next available position if user clicks a button to use it
function useNextAvailablePosition() {
    faq.position = nextAvailablePosition.value
}

onMounted(async () => {
    await fetchAllFaqs()
    if (isEdit.value) {
        await fetchFaq()
    }
})

async function fetchFaq() {
    loading.value = true
    try {
        const response = await axiosClient.get(`/faqs/${faqId.value}/get`)
        if (response.data && response.data.success) {
            const faqData = response.data.data
            faq.title = faqData.title
            faq.content = faqData.content
            faq.position = faqData.position
        }
    } catch (error) {
        console.error('Error fetching FAQ:', error)
        router.push({ name: 'Zoznam FAQ' })
    } finally {
        loading.value = false
    }
}

function validateForm() {
    let isValid = true
    errors.title = ''
    errors.content = ''

    if (!faq.title) {
        errors.title = 'Názov FAQ je povinný'
        isValid = false
    }

    if (!faq.content) {
        errors.content = 'Obsah FAQ je povinný'
        isValid = false
    }

    return isValid
}

async function saveFaq() {
    if (!validateForm()) {
        return
    }

    loading.value = true
    try {        // Prepare the data to send
        const faqData: Record<string, string | number | undefined> = {
            title: faq.title || '',
            content: faq.content || ''
        }

        // Only include position if it has a valid value
        if (faq.position !== null && faq.position !== undefined) {
            faqData.position = Number(faq.position)
        }

        let response
        if (isEdit.value) {
            response = await axiosClient.put(`/faqs/${faqId.value}/update`, faqData)
        } else {
            response = await axiosClient.post('/faqs/add', faqData)
        }

        if (response.data) {
            router.push({ name: 'Zoznam FAQ' })
        }
    } catch (error) {
        console.error('Error saving FAQ:', error)
    } finally {
        loading.value = false
    }
}
</script>
